import Box from "@mui/material/Box";
import { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { useSelector } from "react-redux";

import Card from "@mui/material/Card";
import CardActions from "@mui/material/CardActions";
import Collapse from "@mui/material/Collapse";
import IconButton from "@mui/material/IconButton";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import CircularProgress from "@mui/material/CircularProgress";
import Grid from "@mui/material/Grid";
import CheckBoxIcon from "@mui/icons-material/CheckBox";
import AddIcon from "@mui/icons-material/Add";
import CheckBoxOutlineBlankIcon from "@mui/icons-material/CheckBoxOutlineBlank";
import Filters from "./Filters";
import {
  actionDataGroupUpdated,
  actionDataSelectFeatureRequested,
  roomsToRrns,
  actionDataMaxGroupSizeUpdated,
} from "./dataRedux";
import BuildingFloorsDisplay from "./DisplayBuildingFloors";
import BuildingDisplay from "./DisplayBuilding";
import RoomDisplay from "./DisplayRoom";
import TableTools from "./TableTools";
import { actionAppBarFloorsInSearchUpdated } from "../AppBar";
import { Tooltip, Typography } from "@mui/material";
import { actionSearchVisibleToggled } from "../Search";

const root_ = (theme) => ({
  display: "flex",
  justifyContent: "center",
  flexFlow: "column-reverse",
});

const addSearch_ = (theme) => ({
  textAlign: "center",
  "& .MuiSvgIcon-root": {
    fontSize: "1.25rem",
  },
});

const overlap_ = (theme) => ({
  display: "inline-block",
  padding: "2px",
  marginLeft: "4px",
  border: "2px solid red",
  lineHeight: "1.25",
  fontSize: "0.6rem",
  letterSpacing: "0.01em",
  borderRadius: "4px",
});

const card_ = (theme) => ({
  width: "100%",
  pointerEvents: "auto",
  marginBottom: "4px",
  "& > .MuiCardActions-root": {
    paddingRight: "0px",
  },
});

const expand_ = (theme) => ({
  padding: "0px",
  transform: "rotate(0deg)",
  marginLeft: "auto",
  transition: theme.transitions.create("transform", {
    duration: theme.transitions.duration.shortest,
  }),
});

const expandOpen_ = (theme) => ({
  padding: "0px",
  transform: "rotate(180deg)",
});

const verticalDots_ = (theme) => ({
  padding: "0px",
  borderRadius: "0px",
  width: 20,
  color: "rgba(0, 0, 0, 0.25)",
  "&:hover": {
    color: "rgba(0, 0, 0, 0.55)",
  },
});

const cardActionRoot_ = (theme) => ({
  padding: "2px",
  borderTop: "1px solid rgba(0,0,0,0.1)",
});

const checkbox_ = (theme) => ({
  marginRight: "6px",
});

const lastGrid_ = (theme) => ({
  textAlign: "end",
});

const center_ = (theme) => ({
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
});

export default function SearchResultDisplay({ dataGroups }) {
  return (
    <Box sx={root_}>
      {dataGroups.map((data) => {
        return <DataCard key={data.id} data={data} />;
      })}
      <AddSearchButton />
    </Box>
  );
}

function AddSearchButton() {
  const dispatch = useDispatch();

  const overlapSelection = useSelector(
    (state) => state.dataCardReducer.overlapSelection,
  );

  const onClick = () => {
    dispatch(actionDataMaxGroupSizeUpdated(1));
    dispatch(actionSearchVisibleToggled());
  };

  return (
    <Box sx={addSearch_}>
      <Tooltip title="Add Search">
        <IconButton size="small" onClick={onClick} sx={{ padding: "3px" }}>
          <AddIcon />
        </IconButton>
      </Tooltip>
      {!!overlapSelection.length && (
        <Tooltip
          title={`${overlapSelection.length} overlapping room${overlapSelection.length === 1 ? "" : "s"}`}
        >
          <Typography variant="overline" sx={overlap_}>
            {`${overlapSelection.length} Overlap${overlapSelection.length === 1 ? "" : "s"}`}
          </Typography>
        </Tooltip>
      )}
    </Box>
  );
}

function DataCard({ data }) {
  const [expanded, setExpanded] = useState(true);
  const handleExpandClick = () => {
    setExpanded(!expanded);
  };
  const [toolsExpanded, setToolsExpanded] = useState(false);
  const handleToolToggle = () => {
    setToolsExpanded(!toolsExpanded);
  };

  return (
    <Card sx={card_} variant="outlined">
      <DataCardHeader
        data={data}
        expanded={expanded}
        handleExpandClick={handleExpandClick}
        handleToolToggle={handleToolToggle}
      />
      <DataCardBody
        data={data}
        expanded={expanded}
        toolsExpanded={toolsExpanded}
      />
    </Card>
  );
}

function DataCardHeader({
  data,
  expanded,
  handleExpandClick,
  handleToolToggle,
}) {
  const { id, filters, loading, layerVisible, rooms, color } = data;
  const dispatch = useDispatch();
  const onClick = () => {
    handleExpandClick();
  };
  const toolsOnClick = () => {
    handleToolToggle();
  };

  const checkboxClick = () => {
    if (layerVisible) {
      dispatch(actionDataGroupUpdated({ id, layerVisible: false }));
      dispatch(
        actionDataSelectFeatureRequested({
          type: "room",
          data: [],
          layerId: data.colorLayerId,
        }),
      );
    } else {
      dispatch(actionDataGroupUpdated({ id, layerVisible: true }));
      dispatch(
        actionDataSelectFeatureRequested({
          type: "room",
          data: roomsToRrns(rooms),
          layerId: data.colorLayerId,
        }),
      );
    }
  };

  let loadingIcon = (
    <IconButton size="small">
      <CircularProgress size={24} />
    </IconButton>
  );
  let checkboxIcon = (
    <Tooltip title="Toggle Highlight">
      <IconButton
        size="small"
        onClick={checkboxClick}
        sx={checkbox_}
        data-testid="search-result-highlight-btn"
      >
        {layerVisible ? (
          <CheckBoxIcon data-testid="search-result-highlight-checked" />
        ) : (
          <CheckBoxOutlineBlankIcon data-testid="search-result-highlight-not-checked" />
        )}
      </IconButton>
    </Tooltip>
  );

  return (
    <>
      <CardActions disableSpacing>
        <Grid container>
          <Grid size={{ xs: 1 }} sx={center_}>
            {loading ? loadingIcon : checkboxIcon}
          </Grid>
          <Grid size={{ xs: 9 }}>
            <Filters filters={filters} id={id} />
          </Grid>
          <Grid size={{ xs: 2 }} sx={[lastGrid_, center_]}>
            <Tooltip title="Expand/Collapse">
              <IconButton
                sx={[expand_, expanded && expandOpen_]}
                onClick={onClick}
                aria-expanded={expanded}
                aria-label="show more"
                size="small"
                style={{
                  border: `2px solid ${color}`,
                  backgroundColor: `${color}75`,
                }}
              >
                <ExpandMoreIcon />
              </IconButton>
            </Tooltip>
            <Tooltip title="Options">
              <IconButton
                sx={verticalDots_}
                style={{ visibility: expanded ? "visible" : "hidden" }}
                onClick={toolsOnClick}
                aria-label="show more tools"
                size="small"
              >
                <MoreVertIcon />
              </IconButton>
            </Tooltip>
          </Grid>
        </Grid>
      </CardActions>
    </>
  );
}

function DataCardBody({ data, expanded, toolsExpanded }) {
  const { id, rooms, loading, groupBy, sortBy, sortDirection } = data;
  let dataTable;
  if (groupBy === "building-floor") {
    dataTable = (
      <BuildingFloorsDisplay
        id={id}
        rooms={rooms}
        sortBy={sortBy}
        sortDirection={sortDirection}
      />
    );
  } else if (groupBy === "building-floor-sqft") {
    dataTable = (
      <BuildingDisplay
        id={id}
        rooms={rooms}
        sortBy={sortBy}
        sortDirection={sortDirection}
      />
    );
  } else if (groupBy === "room") {
    dataTable = (
      <RoomDisplay
        id={id}
        rooms={rooms}
        sortBy={sortBy}
        sortDirection={sortDirection}
      />
    );
  } else {
    dataTable = (
      <BuildingFloorsDisplay
        id={id}
        rooms={rooms}
        sortBy={sortBy}
        sortDirection={sortDirection}
      />
    );
  }

  return (
    <>
      <Collapse in={expanded} timeout="auto">
        <Collapse in={toolsExpanded} timeout="auto">
          <TableTools data={data} />
        </Collapse>
        {!!rooms.length && dataTable}
        {!loading && !rooms.length && <NoResult data={data} />}
      </Collapse>
    </>
  );
}

function NoResult({ data }) {
  const dispatch = useDispatch();

  useEffect(() => {
    dispatch(actionAppBarFloorsInSearchUpdated([]));
    dispatch(
      actionDataSelectFeatureRequested({
        type: "room",
        data: [],
        layerId: data.colorLayerId,
      }),
    );
  }, []);

  return (
    <CardActions disableSpacing sx={cardActionRoot_}>
      <Grid container align="center" justifyContent="center">
        No Result
      </Grid>
    </CardActions>
  );
}
